// Booking types for CarYA POS System

export type BookingStatus = 'inquiry' | 'confirmed' | 'active_rental' | 'returned' | 'cancelled';
export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'refunded';
export type PaymentMethod = 'cash' | 'credit_card' | 'debit_card' | 'online_transfer' | 'bank_transfer';
export type VehicleCondition = 'excellent' | 'good' | 'fair' | 'poor';
export type DamageSeverity = 'minor' | 'major' | 'critical';
export type DriverFeeType = 'daily' | 'commission';
export type DriverEmploymentType = 'salaried' | 'external';
export type ProspectCommissionType = 'percentage' | 'fixed';

export interface CustomerContact {
  name: string;
  phone: string;
  email?: string;
  position?: string;
}

export interface CustomerInfo {
  name: string;
  phone: string;
  email?: string;
  dob?: string;
  nationality?: string;
  clientType?: 'client' | 'driver' | 'prospect' | 'company';
  driverRate?: number;
  prospectName?: string;
  prospectPhone?: string;
  prospectCommission?: number;
  companyName?: string;
  contacts?: CustomerContact[];
  licenseNumber?: string;
  /** Legacy field — preserved for old bookings. */
  licenseExpiry?: string;
  /** Date the driving license was issued. */
  licenseIssuedOn?: string;
  /** National ID / CIN / passport number. */
  idCardNumber?: string;
  /** Date the ID card was issued. */
  idCardIssuedOn?: string;
  idProof?: string;
  clientPhoto?: string;
  licensePhoto?: string;
  isCompany?: boolean;
  companyPhone?: string;
  matriculeFiscale?: string;
  address?: {
    street?: string;
    city?: string;
    governorate?: string;
    country?: string;
    postalCode?: string;
  };
}

export interface BookingExtra {
  name: string;
  quantity: number;
  pricePerDay: number;
  numberOfDays: number;
  total: number;
}

export interface LeadInfo {
  company?: string;
  budgetRange?: 'low' | 'medium' | 'high' | 'enterprise';
  projectType?: 'short_term' | 'long_term' | 'corporate' | 'personal';
  preferredVehicle?: PreferredVehicle;
  preferredVehicleId?: string;
}

export interface CommunicationEntry {
  channel: 'email' | 'sms' | 'phone';
  template?: string;
  message: string;
  sentAt: string;
  outcome?: 'no_answer' | 'interested' | 'not_interested' | 'follow_up';
  respondedAt?: string;
  staff?: string;
}

export interface BookingPricing {
  baseRate: number;
  numberOfDays: number;
  subtotal: number;
  htAmount?: number;
  subtotalAfterDiscount?: number;
  priceTimesDaysAmount?: number;
  discountPercentage?: number;
  discountAmount?: number;
  driverRate?: number;
  driverFee?: number;
  driverFeeType?: DriverFeeType;
  driverEmploymentType?: DriverEmploymentType;
  isDriverFeeAddedOnTop?: boolean;
  isDriverFeeTaxable?: boolean;
  showDriverFeeOnInvoice?: boolean;
  showDriverFeeOnContract?: boolean;
  driverExpenseAmount?: number;
  driverFeeLabel?: string;
  prospectCommissionType?: ProspectCommissionType;
  prospectCommissionValue?: number;
  prospectCommissionAmount?: number;
  prospectCommissionTaxable?: boolean;
  prospectCommissionTaxRate?: number;
  prospectCommissionTaxAmount?: number;
  prospectCommissionTaxAddedOnTop?: boolean;
  prospectCommissionNetAmount?: number;
  prospectCommissionExpenseAmount?: number;
  prospectCommissionLabel?: string;
  showProspectCommissionOnInvoice?: boolean;
  extras: BookingExtra[];
  extrasTotal?: number;
  insurance: number;
  baseVatPerDay?: number;
  baseVatAmount?: number;
  baseVatIncludedInPrice?: boolean;
  baseVatAddedAmount?: number;
  baseVatIncludedAmount?: number;
  baseVatLabel?: string;
  baseVatEnabled?: boolean;
  baseVatShowOnInvoice?: boolean;
  timbreFiscalPerDay?: number;
  timbreFiscalAmount?: number;
  timbreFiscalLabel?: string;
  taxIncludedInPrice?: boolean;
  vatRate?: number;
  vatAmount?: number;
  vatIncludedInPrice?: boolean;
  vatAddedAmount?: number;
  vatIncludedAmount?: number;
  vatLabel?: string;
  vatEnabled?: boolean;
  vatShowOnInvoice?: boolean;
  applyVatToExtras?: boolean;
  taxRate: number;
  taxAmount: number;
  taxIncluded?: boolean;
  taxName?: string;
  deposit: number;
  /** Refundable security deposit ("caution") returned at end of rental if no damage. */
  cautionnement?: number;
  cautionnementReceived?: boolean;
  cautionnementReceivedAt?: string;
  cautionnementPaymentMethod?: PaymentMethod;
  cautionnementTransactionId?: string;
  cautionnementPaymentId?: string;
  cautionnementRefunded?: boolean;
  cautionnementNotes?: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  currency?: string;
}

export interface Payment {
  paymentId?: string;
  type?: 'deposit' | 'rental_payment' | 'damage_charge' | 'refund' | 'additional_charge' | 'late_fee';
  amount: number;
  method: PaymentMethod;
  transactionId?: string;
  paidAt: string;
  notes?: string;
  recordedBy?: string;
  balanceCollectionTime?: 'handover' | 'return' | null;
  balanceRemark?: string | null;
}

export interface VehicleDamage {
  description: string;
  severity: DamageSeverity;
  estimatedCost: number;
  photos: string[];
}

export interface HandoverChecklist {
  fuelLevel?: number;
  mileage?: number; // Optional - may not be recorded
  condition?: VehicleCondition;
  photos?: string[];
  notes?: string;
  handedOverBy?: string;
  handedOverAt?: string;
  scheduledHandoverTime?: string;
}

export interface ReturnChecklist {
  fuelLevel: number;
  mileage?: number; // Optional - only required if handover mileage was recorded
  condition: VehicleCondition;
  damages: VehicleDamage[];
  photos: string[];
  notes?: string;
  receivedBy: string;
  receivedAt: string;
  lateFee: number;
  damageCharge: number;
  fuelCharge: number;
  cleaningFee: number;
}

export interface Booking {
  _id: string;
  bookingNumber: string;
  agencyId: string;
  vehicleId: string | {
    _id: string;
    make: string;
    model: string;
    year?: number;
    plateNumber: string;
    category: string;
    pricePerDay?: number;
    images: string[];
    mileage?: number;
  };
  customer: CustomerInfo;
  startDate: string;
  endDate: string;
  pickupLocation: string;
  dropoffLocation: string;
  assignedDriverId?: string | {
    _id: string;
    name: string;
    phone: string;
    email?: string;
  };
  driverRate?: number;
  isDriverFeeAddedOnTop?: boolean;
  showDriverFeeOnInvoice?: boolean;
  showDriverFeeOnContract?: boolean;
  isDriverFeeTaxable?: boolean;
  driverFeeType?: DriverFeeType;
  driverEmploymentType?: DriverEmploymentType;
  pricing: BookingPricing;
  additionalDrivers?: CustomerInfo[];
  secondDriver?: CustomerInfo; // Legacy
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  paymentMethod?: string;
  paymentTiming?: 'at_confirmation' | 'before_handover' | 'after_return';
  balanceCollectionTiming?: 'handover' | 'return';
  payments: Payment[];
  leadInfo?: LeadInfo;
  marketingConsent?: boolean;
  communicationHistory?: CommunicationEntry[];
  leadScore?: number;
  leadStatus?: 'cold' | 'warm' | 'hot';
  nextAction?: string;
  nextActionDueAt?: string;
  handoverChecklist?: HandoverChecklist;
  returnChecklist?: ReturnChecklist;
  contractUrl?: string;
  contractReference?: string;
  signedAt?: string;
  termsAccepted: boolean;
  specialRequests?: string;
  internalNotes?: string;
  quotationId?: string;
  quotationNumber?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  cancelledBy?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Virtuals
  durationDays?: number;
  isOverdue?: boolean;
  daysUntilStart?: number;
}

export interface BookingFormData {
  vehicleId: string;
  customer: CustomerInfo;
  startDate: string;
  endDate: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  additionalDrivers?: CustomerInfo[];
  secondDriver?: CustomerInfo; // Legacy
  extras?: Array<{
    name: string;
    quantity: number;
    pricePerDay: number;
  }>;
  pricing?: Partial<PricingCalculation>;
  insurance?: number;
  taxRate?: number;
  discountPercentage?: number;
  assignedDriverId?: string;
  driverRate?: number;
  isDriverFeeAddedOnTop?: boolean;
  showDriverFeeOnInvoice?: boolean;
  showDriverFeeOnContract?: boolean;
  /** True when the driver fee is part of the VAT base. Defaults to false (external = tax-free). */
  isDriverFeeTaxable?: boolean;
  /** Distinguishes a salaried driver (fee included in rent, taxed as part of rent) from an external one. */
  driverFeeType?: DriverFeeType;
  driverEmploymentType?: DriverEmploymentType;
  paymentTiming?: 'at_confirmation' | 'before_handover' | 'after_return';
  paymentAmountType?: 'full' | '50_percent' | 'custom';
  paymentMode?: PaymentMethod;
  transactionId?: string;
  paidAmount?: number;
  specialRequests?: string;
  internalNotes?: string;
  leadInfo?: LeadInfo;
  marketingConsent?: boolean;
  communicationHistory?: CommunicationEntry[];
  leadStatus?: 'cold' | 'warm' | 'hot';
  nextAction?: string;
  nextActionDueAt?: string;
  contractReference?: string;
}

export interface BookingListFilters {
  status?: BookingStatus;
  excludeStatus?: BookingStatus;
  startDate?: string;
  endDate?: string;
  customerPhone?: string;
  vehicleId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface BookingListResponse {
  success: boolean;
  message: string;
  data: {
    bookings: Booking[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

export interface BookingResponse {
  success: boolean;
  message: string;
  data: Booking;
}

export interface PricingCalculation {
  baseRate: number;
  numberOfDays: number;
  subtotal: number;
  htAmount?: number;
  subtotalAfterDiscount?: number;
  priceTimesDaysAmount?: number;
  discountPercentage?: number;
  discountAmount?: number;
  driverRate?: number;
  driverFee?: number;
  driverFeeType?: DriverFeeType;
  isDriverFeeAddedOnTop?: boolean;
  showDriverFeeOnInvoice?: boolean;
  showDriverFeeOnContract?: boolean;
  isDriverFeeTaxable?: boolean;
  driverEmploymentType?: DriverEmploymentType;
  driverExpenseAmount?: number;
  driverFeeLabel?: string;
  prospectCommissionType?: ProspectCommissionType;
  prospectCommissionValue?: number;
  prospectCommissionAmount?: number;
  prospectCommissionTaxable?: boolean;
  prospectCommissionTaxRate?: number;
  prospectCommissionTaxAmount?: number;
  prospectCommissionTaxAddedOnTop?: boolean;
  prospectCommissionNetAmount?: number;
  prospectCommissionExpenseAmount?: number;
  prospectCommissionLabel?: string;
  showProspectCommissionOnInvoice?: boolean;
  extras: BookingExtra[];
  extrasTotal: number;
  baseVatPerDay?: number;
  baseVatAmount?: number;
  baseVatIncludedInPrice?: boolean;
  baseVatAddedAmount?: number;
  baseVatIncludedAmount?: number;
  baseVatLabel?: string;
  baseVatEnabled?: boolean;
  baseVatShowOnInvoice?: boolean;
  timbreFiscalPerDay?: number;
  timbreFiscalAmount?: number;
  timbreFiscalLabel?: string;
  taxIncludedInPrice?: boolean;
  vatRate?: number;
  vatAmount?: number;
  vatIncludedInPrice?: boolean;
  vatAddedAmount?: number;
  vatIncludedAmount?: number;
  vatLabel?: string;
  vatEnabled?: boolean;
  vatShowOnInvoice?: boolean;
  applyVatToExtras?: boolean;
  taxRate: number;
  taxAmount: number;
  taxIncluded?: boolean;
  taxName?: string;
  deposit: number;
  /** Refundable security deposit, set at checkout. */
  cautionnement?: number;
  cautionnementReceived?: boolean;
  cautionnementReceivedAt?: string;
  cautionnementPaymentMethod?: PaymentMethod;
  cautionnementTransactionId?: string;
  cautionnementPaymentId?: string;
  cautionnementNotes?: string;
  manualTotalAmount?: number;
  totalAmount: number;
  currency: string;
}

export interface PricingRequest {
  vehicleId: string;
  startDate: string;
  endDate: string;
  extras?: Array<{
    name: string;
    quantity: number;
    pricePerDay: number;
  }>;
  taxRate?: number;
  discountPercentage?: number;
  driverData?: {
    driverRate?: number;
    driverFeeType?: DriverFeeType;
    isDriverFeeAddedOnTop?: boolean;
    showDriverFeeOnInvoice?: boolean;
    showDriverFeeOnContract?: boolean;
    isDriverFeeTaxable?: boolean;
    driverEmploymentType?: DriverEmploymentType;
  };
}

export interface AvailabilityCheck {
  available: boolean;
  conflicts: Booking[];
}

export interface DashboardStats {
  today: {
    pickupsToday: number;
    returnsToday: number;
    activeRentals: number;
    todayRevenue: number;
  };
  upcoming: Booking[];
  overdue: Booking[];
  revenue: {
    total: number;
    byType: {
      [key: string]: {
        total: number;
        count: number;
      };
    };
    count: number;
  };
  overview?: {
    activeBookingsCount: number;
    todaysRevenue: number;
    totalRevenue: number;
    averageBookingValue: number;
    repeatRate: number;
  };
  activity?: {
    recentBookings: Booking[];
    handoverQueue: Booking[];
    upcomingCollections: Booking[];
    alertBookings: Booking[];
  };
}

export interface AnalyticsData {
  period: {
    start: string;
    end: string;
  };
  bookings: Array<{
    _id: BookingStatus;
    count: number;
    totalRevenue: number;
  }>;
  revenue: Array<{
    _id: {
      type: string;
      method: string;
    };
    total: number;
    count: number;
  }>;
  vehicleUtilization: Array<{
    _id: string;
    bookings: number;
    revenue: number;
  }>;
}

// Constants - Minimal Clean Palette
export const BOOKING_STATUS_COLORS: Record<BookingStatus, string> = {
  inquiry: '#9CA3AF',          // Muted gray - waiting
  confirmed: '#1F2937',        // Dark accent - confirmed
  active_rental: '#1F2937',    // Dark accent - active
  returned: '#22C55E',         // Success green - completed
  cancelled: '#9CA3AF'         // Muted gray - cancelled
};

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  inquiry: 'Inquiry',
  confirmed: 'Confirmed',
  active_rental: 'Active Rental',
  returned: 'Returned',
  cancelled: 'Cancelled'
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  unpaid: '#EF4444',      // Error red - needs attention
  partial: '#F59E0B',     // Warning amber - partial
  paid: '#22C55E',        // Success green - complete
  refunded: '#9CA3AF'     // Muted gray - refunded
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: 'Unpaid',
  partial: 'Partial',
  paid: 'Paid',
  refunded: 'Refunded'
};

export const CONDITION_COLORS: Record<VehicleCondition, string> = {
  excellent: '#22C55E',   // Success green
  good: '#3B82F6',        // Info blue
  fair: '#F59E0B',        // Warning amber
  poor: '#EF4444'         // Error red
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  online_transfer: 'Online Transfer',
  bank_transfer: 'Bank Transfer'
};

export const isBookingExtended = (booking: Pick<Booking, 'internalNotes'>): boolean =>
  Boolean(booking.internalNotes?.includes('Extended from'));

export interface PreferredVehicle {
  make: string;
  model: string;
  year: number;
  plateNumber?: string;
  vin?: string;
}
